import { Calendar, User } from "lucide-react";
import React, { type Dispatch, type SetStateAction } from "react";

import type { SummaryDraft } from "../types";
import { SummaryAccidentDetailsField } from "./fields/SummaryAccidentDetailsField";
import { SummaryPatientField } from "./fields/SummaryPatientField";

export interface SummaryPatientSectionProps {
  isEditMode: boolean;
  summaryDraft: SummaryDraft;
  setSummaryDraft: Dispatch<SetStateAction<SummaryDraft>>;
  highlightQuery: string;
}

export const SummaryPatientSection: React.FC<SummaryPatientSectionProps> = ({
  isEditMode,
  summaryDraft,
  setSummaryDraft,
  highlightQuery,
}) => (
  <section className="overflow-hidden rounded-xl border border-neutral-100 bg-card shadow-sm">
    <div className="flex items-center gap-2.5 border-b border-neutral-100 bg-gradient-to-r from-info-subtle/60 via-card to-card px-5 py-3.5">
      <div className="flex items-center justify-center rounded-lg bg-info-subtle-strong/70 p-1.5 text-info">
        <User size={14} strokeWidth={2.5} />
      </div>
      <div>
        <h3 className="text-xs font-bold tracking-tight text-neutral-900">Patient Information</h3>
        <p className="mt-0.5 text-2xs font-medium text-neutral-500">
          Demographics &amp; incident details
        </p>
      </div>
    </div>

    <div className="space-y-5 p-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <SummaryPatientField
          icon={<User size={12} strokeWidth={2.5} />}
          label="Patient Name"
          value={summaryDraft.patient_name}
          isEditMode={isEditMode}
          onChange={(v) => setSummaryDraft((prev) => ({ ...prev, patient_name: v || null }))}
          placeholder="Add patient name"
          highlightQuery={highlightQuery}
        />
        <SummaryPatientField
          icon={<Calendar size={12} strokeWidth={2.5} />}
          label="Date of Birth"
          value={summaryDraft.date_of_birth}
          isEditMode={isEditMode}
          onChange={(v) => setSummaryDraft((prev) => ({ ...prev, date_of_birth: v || null }))}
          placeholder="Add date of birth"
          type="date"
          highlightQuery={highlightQuery}
        />
        <SummaryPatientField
          icon={<Calendar size={12} strokeWidth={2.5} />}
          label="Date of Injury"
          value={summaryDraft.date_of_injury}
          isEditMode={isEditMode}
          onChange={(v) => setSummaryDraft((prev) => ({ ...prev, date_of_injury: v || null }))}
          placeholder="Add date of injury"
          type="date"
          highlightQuery={highlightQuery}
        />
      </div>
      <div className="border-t border-neutral-100" />
      <SummaryAccidentDetailsField
        value={summaryDraft.accident_description}
        isEditMode={isEditMode}
        onChange={(v) => setSummaryDraft((prev) => ({ ...prev, accident_description: v || null }))}
        placeholder="Add accident description and clinical context…"
        highlightQuery={highlightQuery}
      />
    </div>
  </section>
);
